import { Link } from "react-router-dom";
import { Crown, LogOut, LogIn, ExternalLink, ArrowLeft, Loader2, Download, Trash2, Gift, Copy, Check, Share2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { usePremium, startLogin, logoutRequest, openPortal, exportAccount, deleteAccount, fetchReferral } from "@/context/PremiumContext";
import { PlanCards } from "@/pages/Premium";

function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    fetchReferral()
      .then((d) => active && setData(d))
      .catch((e) => console.debug("Parrainage indisponible:", e));
    return () => { active = false; };
  }, []);

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Lien de parrainage copié");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.debug("Copie impossible:", e);
      toast.error("Copie impossible");
    }
  };

  const share = async () => {
    const shareData = {
      title: "vspo — GPS voiture sans permis",
      text: "Rejoins-moi sur vspo, le GPS pour voiture sans permis. On gagne chacun un mois Premium offert !",
      url: link,
    };
    try {
      if (navigator.share) await navigator.share(shareData);
      else await copy();
    } catch (e) {
      console.debug("Partage annulé:", e);
    }
  };

  return (
    <Card className="space-y-3 border-primary/30 p-5" data-testid="referral-card">
      <div className="flex items-center gap-2 text-primary">
        <Gift className="h-5 w-5" />
        <span className="font-semibold">Parrainage — 1 mois offert</span>
      </div>
      <p className="text-sm text-muted-foreground">
        Partagez votre lien. Quand un ami s'abonne, vous recevez <strong>chacun un mois gratuit</strong>.
      </p>

      <div className="flex gap-2">
        <Input readOnly value={link} aria-label="Lien de parrainage" data-testid="referral-link-input" />
        <Button variant="outline" onClick={copy} disabled={!link} aria-label="Copier le lien" data-testid="referral-copy-button">
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
        </Button>
        <Button onClick={share} disabled={!link} data-testid="referral-share-button">
          <Share2 className="mr-2 h-4 w-4" /> Partager
        </Button>
      </div>

      <div className="flex gap-3 pt-1">
        <div className="flex-1 rounded-lg bg-muted/60 p-3 text-center" data-testid="referral-count">
          <div className="text-xl font-extrabold">{data ? data.referral_count : 0}</div>
          <div className="text-xs text-muted-foreground">Filleuls abonnés</div>
        </div>
        <div className="flex-1 rounded-lg bg-muted/60 p-3 text-center" data-testid="referral-free-months">
          <div className="text-xl font-extrabold">{data ? data.free_months_total : 0}</div>
          <div className="text-xs text-muted-foreground">Mois offerts</div>
        </div>
      </div>
      {data && data.pending_free_months > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="referral-pending">
          {data.pending_free_months} mois offert(s) en attente — appliqué(s) dès votre prochain abonnement.
        </p>
      )}
    </Card>
  );
}

export default function Account() {
  const { user, loading, isAuthenticated, isPremium, refresh } = usePremium();
  const [busy, setBusy] = useState(false);

  const doLogout = async () => {
    setBusy(true);
    try {
      await logoutRequest();
      await refresh();
      toast.success("Déconnecté");
    } catch (e) {
      toast.error("Erreur de déconnexion");
    } finally {
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { portal_url } = await openPortal();
      if (portal_url) window.location.href = portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible");
      setBusy(false);
    }
  };

  const doExport = async () => {
    try {
      const data = await exportAccount();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vspo-mes-donnees.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Export impossible");
    }
  };

  const doDelete = async () => {
    try {
      await deleteAccount();
      await refresh();
      toast.success("Compte supprimé");
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-10" data-testid="account-page">
      <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Retour au GPS
      </Link>
      <h1 className="mb-6 text-2xl font-extrabold">Mon compte</h1>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : !isAuthenticated ? (
        <Card className="p-6" data-testid="login-card">
          <p className="mb-4 text-sm text-muted-foreground">
            Connectez-vous pour gérer votre abonnement Premium et le retrouver sur tous vos appareils.
          </p>
          <Button onClick={startLogin} className="w-full" data-testid="login-button">
            <LogIn className="mr-2 h-4 w-4" /> Se connecter avec Google
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card className="flex items-center gap-4 p-5" data-testid="profile-card">
            <Avatar className="h-12 w-12">
              {user.picture ? <AvatarImage src={user.picture} alt={user.name || user.email} /> : null}
              <AvatarFallback>{(user.name || user.email || "?").slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{user.name || "Utilisateur"}</div>
              <div className="truncate text-sm text-muted-foreground">{user.email}</div>
            </div>
            {isPremium && (
              <Badge className="gap-1 bg-amber-500 text-black hover:bg-amber-500"><Crown className="h-3 w-3" /> Premium</Badge>
            )}
          </Card>

          {isPremium ? (
            <Card className="space-y-3 border-amber-500/40 p-5" data-testid="premium-active-card">
              <div className="flex items-center gap-2 text-amber-500">
                <Crown className="h-5 w-5" /><span className="font-semibold">Abonnement Premium actif</span>
              </div>
              <p className="text-sm text-muted-foreground">Formule : {user.plan === "vspo_premium_yearly_eur" ? "Annuel" : "Mensuel"}</p>
              <Button variant="outline" onClick={manage} disabled={busy} data-testid="manage-subscription-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                Gérer / résilier mon abonnement
              </Button>
            </Card>
          ) : (
            <div>
              <h2 className="mb-3 text-lg font-bold">Passer Premium</h2>
              <PlanCards />
            </div>
          )}

          <ReferralCard />

          <Card className="space-y-3 p-5" data-testid="rgpd-card">
            <div className="text-sm font-semibold">Mes données (RGPD)</div>
            <p className="text-xs text-muted-foreground">
              Exportez vos données personnelles ou supprimez définitivement votre compte.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={doExport} data-testid="export-data-button">
                <Download className="mr-2 h-4 w-4" /> Exporter mes données
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="delete-account-button">
                    <Trash2 className="mr-2 h-4 w-4" /> Supprimer mon compte
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-account-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer définitivement votre compte ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Vos données de compte (profil, zones favorites) seront effacées. Si vous avez un abonnement
                      actif, résiliez-le d'abord via « Gérer mon abonnement ». Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-account">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>

          <Button variant="ghost" onClick={doLogout} disabled={busy} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Se déconnecter
          </Button>
        </div>
      )}
    </div>
  );
}
